import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle, Gift, Home, Loader2, Mail } from "lucide-react";
import { BRAND } from "@/config/brand";
import regardLogo from "@/assets/regard-de-nature-logo.png";

export default function GiftCardSuccess() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [giftCard, setGiftCard] = useState<any>(null);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const giftCardId = searchParams.get("gift_card_id");

    if (sessionId && giftCardId) {
      confirmPayment(sessionId, giftCardId);
    } else {
      setError("Paramètres manquants");
      setLoading(false);
    }
  }, [searchParams]);

  const confirmPayment = async (sessionId: string, giftCardId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke("confirm-gift-card-payment", {
        body: { sessionId, giftCardId },
      });

      if (error) throw error;

      if (data?.success) {
        setGiftCard(data.giftCard);
      } else {
        throw new Error("Payment confirmation failed");
      }
    } catch (err: any) {
      console.error("Error confirming payment:", err);
      setError(err.message || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-primary">Confirmation en cours...</h2>
            <p className="text-muted-foreground mt-2">
              Nous confirmons votre paiement et préparons la carte cadeau.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Gift className="w-8 h-8 text-destructive" />
            </div>
            <h2 className="text-xl font-semibold text-destructive">Erreur</h2>
            <p className="text-muted-foreground mt-2">{error}</p>
            <Link to="/">
              <Button className="mt-6">
                <Home className="mr-2 w-4 h-4" />
                Retour à l'accueil
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const expiresDate = giftCard?.expires_at
    ? new Date(giftCard.expires_at).toLocaleDateString("fr-FR", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "";

  return (
    <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
      <Card className="max-w-lg w-full overflow-hidden border-0 shadow-xl">
        <div className="bg-gradient-to-r from-primary to-primary/80 p-6 text-center">
          <img src={regardLogo} alt={BRAND.name.full} className="w-16 h-16 mx-auto mb-3" />
          <h1 className="text-2xl font-playfair font-bold text-white">{BRAND.name.full}</h1>
          <p className="text-secondary font-medium tracking-widest text-sm">{BRAND.baseline.full}</p>
        </div>

        <CardContent className="p-8 text-center space-y-6">
          <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle className="w-12 h-12 text-primary" />
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-primary">Merci pour votre achat !</h2>
            <p className="text-muted-foreground mt-2">
              Votre carte cadeau a été créée et envoyée avec succès.
            </p>
          </div>

          <div className="bg-secondary/10 p-6 rounded-xl space-y-3">
            <p className="text-sm text-muted-foreground">Montant de la carte cadeau</p>
            <p className="text-4xl font-bold text-primary">{giftCard?.amount}€</p>
            
            <div className="pt-3 border-t border-border">
              <p className="text-sm text-muted-foreground">Envoyée à</p>
              <p className="font-semibold text-primary">{giftCard?.recipient_name}</p>
              <p className="text-sm text-muted-foreground">{giftCard?.recipient_email}</p>
            </div>
          </div>

          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground bg-muted/50 p-4 rounded-lg">
            <Mail className="w-4 h-4" />
            <span>
              Un email de confirmation a été envoyé au destinataire et à vous-même.
            </span>
          </div>

          <p className="text-sm text-muted-foreground">
            Carte cadeau valable jusqu'au <strong>{expiresDate}</strong>
          </p>

          <Link to="/">
            <Button className="w-full h-12 bg-primary hover:bg-primary/90">
              <Home className="mr-2 w-4 h-4" />
              Retour à l'accueil
            </Button>
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}
